import { useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import BYDBanner from './Asset/banner-DenzaD9.webp';
import BYDBannerMobile from './Asset/denzad9-banner-mob.webp';
import DenzaD9Black from './Asset/Denza_D9_EV-black.webp';
import DenzaD9White from './Asset/Denza_D9_EV-white.webp';
import DenzaD9Grey from './Asset/Denza_D9_EV-grey.webp';
import DenzaD9Blue from './Asset/Denza_D9_EV-blue.webp';
import DenzaD9Interior from './Asset/interior.webp';
import DenzaD9Sunroof from './Asset/m07-pc-skylight.webp';
import DenzaD9beige from './Asset/interior-beige.webp';
import DenzaD9brown from './Asset/interior-brown.webp';
import PageSeo, {
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from './PageSeo';

function JelajahiDenza() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [selectedColor, setSelectedColor] = useState('black');
  const [selectedInterior, setSelectedInterior] = useState('beige');

  const colorOptions = useMemo(
    () => [
      {
        label: 'Black',
        value: 'black',
        swatch: '#111111',
        image: DenzaD9Black,
      },
      {
        label: 'White',
        value: 'white',
        swatch: '#f4f4f1',
        image: DenzaD9White,
      },
      {
        label: 'Grey',
        value: 'grey',
        swatch: '#8d9298',
        image: DenzaD9Grey,
      },
      {
        label: 'Blue',
        value: 'blue',
        swatch: '#23385f',
        image: DenzaD9Blue,
      },
    ],
    [],
  );

  const interiorOptions = useMemo(
    () => [
      {
        label: 'Beige',
        value: 'beige',
        swatch: 'linear-gradient(135deg, #efe3cd 0%, #c9ae89 100%)',
        image: DenzaD9beige,
      },
      {
        label: 'Brown',
        value: 'brown',
        swatch: 'linear-gradient(135deg, #7a4e35 0%, #2b1f1a 100%)',
        image: DenzaD9brown,
      },
    ],
    [],
  );

  const currentColor =
    colorOptions.find((option) => option.value === selectedColor) ||
    colorOptions[0];
  const currentInterior =
    interiorOptions.find((option) => option.value === selectedInterior) ||
    interiorOptions[0];

  return (
    <>
      <PageSeo
        title='Danza D9 BSD Serpong | Harga MPV Listrik Premium'
        description='Jelajahi Danza D9 di BYD BSD Serpong. Cek harga MPV listrik premium, spesifikasi, interior captain seat, promo, dan test drive.'
        keywords={[
          ...keywordSets.localDealer,
          'harga Danza D9 BSD',
          'Danza D9 Tangerang',
          'MPV listrik premium',
          'Danza D9 Indonesia',
        ]}
        path='/jelajahi-denzad9'
        image={BYDBanner}
        jsonLd={[
          productJsonLd({
            name: 'Danza D9',
            description:
              'MPV listrik premium Danza D9 dengan kabin mewah, captain seat, dan jarak tempuh panjang untuk keluarga.',
            image: BYDBanner,
            path: '/jelajahi-denzad9',
            price: 950000000,
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Danza D9', path: '/jelajahi-denzad9' },
          ]),
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <Box
          component='h1'
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          Jelajahi Danza D9
        </Box>

        <HeroSection isMobile={isMobile} />

        <VehicleColorSection
          currentColor={currentColor}
          colorOptions={colorOptions}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
        />

        <InteriorColorSection
          currentInterior={currentInterior}
          interiorOptions={interiorOptions}
          selectedInterior={selectedInterior}
          setSelectedInterior={setSelectedInterior}
        />

        <FeatureImageSection
          image={DenzaD9Sunroof}
          title='Panoramic Sunroof'
          description='Sunroof panoramik ekstra besar membawa cahaya alami ke dalam kabin, membuat ruang terasa lapang, terang, dan tenang untuk perjalanan jauh.'
          imageFit='cover'
        />

        <FeatureImageSection
          image={DenzaD9Interior}
          title='Executive Cabin'
          description='Kabin Danza D9 dirancang untuk kenyamanan keluarga dan perjalanan bisnis, dengan ruang luas, material premium, dan suasana interior yang tenang.'
          imageFit='cover'
        />

        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection({ isMobile }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '92vh', md: '100vh' },
        pt: { xs: 9, md: 10 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundColor: '#0f1115',
      }}
    >
      <Box
        component='img'
        src={isMobile ? BYDBannerMobile : BYDBanner}
        alt='Danza D9 premium electric MPV'
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: { xs: 'cover', md: 'cover' },
          objectPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.12) 44%, rgba(0,0,0,0.58) 100%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          alignSelf: 'flex-end',
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '2.25rem', sm: '3.2rem', md: '4.5rem' },
            fontWeight: 900,
            lineHeight: 1,
            maxWidth: 760,
          }}
        >
          Danza D9
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 650,
            fontSize: { xs: '1rem', md: '1.2rem' },
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          MPV listrik premium dengan kabin luas, kenyamanan executive, dan
          teknologi cerdas untuk keluarga modern.
        </Typography>

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1,
            maxWidth: 760,
          }}
        >
          {[
            { value: '600 km', label: 'NEDC range' },
            { value: '230 kW', label: 'Tenaga maksimum' },
            { value: '7 kursi', label: 'Premium cabin' },
          ].map((spec) => (
            <Box
              key={spec.label}
              sx={{
                border: '1px solid rgba(255,255,255,0.26)',
                backgroundColor: 'rgba(0,0,0,0.36)',
                px: 2,
                py: 1.5,
                borderRadius: '8px',
              }}
            >
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800 }}>
                {spec.value}
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', opacity: 0.82 }}>
                {spec.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function VehicleColorSection({
  currentColor,
  colorOptions,
  selectedColor,
  setSelectedColor,
}) {
  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        backgroundColor: '#f4f6f8',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' },
          gap: { xs: 4, lg: 5 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#4c5867',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.82rem',
            }}
          >
            Exterior Color
          </Typography>
          <Typography
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.05,
            }}
          >
            Pilih warna Danza D9
          </Typography>
          <Box
            sx={{
              mt: 2,
              height: { xs: 260, sm: 360, md: 460 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component='img'
              src={currentColor.image}
              alt={`Danza D9 warna ${currentColor.label}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: 'drop-shadow(0 26px 28px rgba(16,24,40,0.22))',
              }}
            />
          </Box>
        </Box>

        <SelectorPanel
          eyebrow='Selected exterior'
          title={currentColor.label}
          description='Pilih warna eksterior yang paling cocok dengan karakter pemakaian harian atau perjalanan bisnis.'
          options={colorOptions}
          selectedValue={selectedColor}
          onSelect={setSelectedColor}
        />
      </Box>
    </Box>
  );
}

function InteriorColorSection({
  currentInterior,
  interiorOptions,
  selectedInterior,
  setSelectedInterior,
}) {
  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        backgroundColor: '#111827',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '360px 1fr' },
          gap: { xs: 4, lg: 5 },
          alignItems: 'center',
        }}
      >
        <SelectorPanel
          eyebrow='Selected interior'
          title={currentInterior.label}
          description='Pilih nuansa kabin untuk pengalaman berkendara yang lebih personal.'
          options={interiorOptions}
          selectedValue={selectedInterior}
          onSelect={setSelectedInterior}
          dark
        />

        <Box>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.72)',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.82rem',
            }}
          >
            Interior Color
          </Typography>
          <Typography
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              color: '#fff',
              lineHeight: 1.05,
            }}
          >
            Kabin yang terasa tenang
          </Typography>
          <Box
            sx={{
              mt: 3,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 24px 42px rgba(0,0,0,0.34)',
              backgroundColor: '#0b0f16',
            }}
          >
            <Box
              component='img'
              src={currentInterior.image}
              alt={`Interior Danza D9 ${currentInterior.label}`}
              sx={{
                width: '100%',
                height: { xs: 280, sm: 420, md: 540 },
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SelectorPanel({
  eyebrow,
  title,
  description,
  options,
  selectedValue,
  onSelect,
  dark = false,
}) {
  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: dark
          ? '1px solid rgba(255,255,255,0.18)'
          : '1px solid rgba(17,24,39,0.12)',
        backgroundColor: dark ? 'rgba(255,255,255,0.06)' : '#fff',
        color: dark ? '#fff' : '#111827',
        p: { xs: 2, sm: 3 },
        boxShadow: dark ? 'none' : '0 12px 26px rgba(16,24,40,0.08)',
      }}
    >
      <Typography
        sx={{
          color: dark ? 'rgba(255,255,255,0.66)' : '#667085',
          fontWeight: 800,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          fontSize: '0.78rem',
        }}
      >
        {eyebrow}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: '2rem', fontWeight: 900 }}>
        {title}
      </Typography>
      <Typography
        sx={{
          mt: 1,
          lineHeight: 1.6,
          color: dark ? 'rgba(255,255,255,0.72)' : '#4b5563',
        }}
      >
        {description}
      </Typography>

      <Box sx={{ mt: 3, display: 'grid', gap: 1 }}>
        {options.map((option) => {
          const active = selectedValue === option.value;

          return (
            <Button
              key={option.value}
              type='button'
              onClick={() => onSelect(option.value)}
              variant='outlined'
              sx={{
                justifyContent: 'flex-start',
                gap: 1.5,
                py: 1.2,
                px: 1.3,
                borderRadius: '8px',
                textTransform: 'none',
                color: dark ? '#fff' : '#111827',
                borderColor: active
                  ? '#2d6cdf'
                  : dark
                    ? 'rgba(255,255,255,0.22)'
                    : 'rgba(17,24,39,0.16)',
                backgroundColor: active
                  ? dark
                    ? 'rgba(45,108,223,0.22)'
                    : '#edf4ff'
                  : 'transparent',
                '&:hover': {
                  borderColor: '#2d6cdf',
                  backgroundColor: active
                    ? dark
                      ? 'rgba(45,108,223,0.28)'
                      : '#e4efff'
                    : dark
                      ? 'rgba(255,255,255,0.08)'
                      : '#f7f9fc',
                },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  flex: '0 0 auto',
                  background: option.swatch,
                  border:
                    option.value === 'white'
                      ? '1px solid rgba(17,24,39,0.24)'
                      : '1px solid rgba(255,255,255,0.42)',
                  boxShadow: active ? '0 0 0 3px rgba(45,108,223,0.22)' : '',
                }}
              />
              <Typography sx={{ fontWeight: 800 }}>{option.label}</Typography>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

function FeatureImageSection({ image, title, description, imageFit }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '82vh', md: '100vh' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        backgroundColor: '#0f1115',
      }}
    >
      <Box
        component='img'
        src={image}
        alt={title}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: imageFit,
          objectPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.62) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 980,
          px: { xs: 2, md: 6 },
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3.5rem' },
            fontWeight: 900,
            lineHeight: 1.05,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 720,
            fontSize: { xs: '1rem', md: '1.12rem' },
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.88)',
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f6f8',
      }}
    >
      <Box
        sx={{
          maxWidth: 980,
          mx: 'auto',
          textAlign: 'center',
        }}
      >
        <Typography
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
          }}
        >
          Tertarik dengan Danza D9?
        </Typography>
        <Typography
          sx={{
            mt: 2,
            mx: 'auto',
            maxWidth: 780,
            color: '#4b5563',
            lineHeight: 1.7,
          }}
        >
          Hubungi Junius di BYD BSD Serpong untuk info harga Danza D9, promo
          terbaru, simulasi pembelian, dan jadwal test drive.
        </Typography>
        <Button
          href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20tertarik%20dengan%20Denza%20D9%20dan%20ingin%20tahu%20harga%20serta%20promonya'
          target='_blank'
          variant='contained'
          sx={{
            mt: 3,
            px: 4,
            py: 1.25,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: '#1f5fd1',
            '&:hover': { backgroundColor: '#184ca8' },
          }}
        >
          Tanya Harga Danza D9
        </Button>
      </Box>
    </Box>
  );
}

export default JelajahiDenza;
